import { ChevronDown, ChevronRight, FileText, Pencil, Trash2, User } from 'lucide-react';
import { checkOverdueStatus, convertUTCToBrazilianDate, formatCreationDate } from '@/utils/dateUtils';
import { POR_EVENTO_CONDITIONAL_TYPE, entryCountsInOperationalFinance } from '@/utils/financialOperationalImpact';

interface FinancialEntryRowProps {
  entry: any;
  onEdit: (entry: any) => void;
  onDelete: (id: string) => void;
  onOpenProcess: (entry: any) => void;
  /** Parcelamento consolidado que pode ser expandido */
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

const brl = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

const statusStyle = (status: string) => {
  switch (status) {
    case 'Pago':
      return 'bg-emerald-50 text-emerald-700';
    case 'Pendente':
      return 'bg-amber-50 text-amber-700';
    case 'Vencido':
      return 'bg-red-50 text-red-700';
    case 'Em Negociação':
      return 'bg-orange-50 text-orange-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

/** Linha de um lançamento: título e selos à esquerda, detalhes embaixo; valor e ações à direita. */
export const FinancialEntryRow = ({
  entry,
  onEdit,
  onDelete,
  onOpenProcess,
  expandable,
  expanded,
  onToggleExpand,
}: FinancialEntryRowProps) => {
  const isRevenue = entry.entry_type === 'receita';
  const isPaid = entry.status === 'Pago';
  const isByEvent = entry.is_conditional_payment && entry.conditional_type === POR_EVENTO_CONDITIONAL_TYPE;
  const processTitle = entry.cases?.title || (entry.administrative_process_id ? 'Processo administrativo' : null);

  // Situação do pagamento em uma frase curta
  let dateInfo: { text: string; className: string } | null = null;
  if (isPaid && (entry.actual_payment_date || entry.payment_date)) {
    dateInfo = { text: `Pago em ${convertUTCToBrazilianDate(entry.actual_payment_date || entry.payment_date)}`, className: 'text-emerald-700' };
  } else if (entry.payment_date) {
    const { isOverdue, overdueDays } = checkOverdueStatus(entry.payment_date);
    dateInfo = isOverdue && entry.status === 'Pendente'
      ? { text: `Venceu em ${convertUTCToBrazilianDate(entry.payment_date)} · ${overdueDays} ${overdueDays === 1 ? 'dia' : 'dias'} em atraso`, className: 'font-medium text-red-600' }
      : { text: `Vence em ${convertUTCToBrazilianDate(entry.payment_date)}`, className: 'text-amber-700' };
  } else if (!isPaid) {
    dateInfo = { text: 'Sem data definida', className: 'text-slate-400' };
  }

  return (
    <div className="flex items-start gap-4" title={entry.created_at ? `Criado em ${formatCreationDate(entry.created_at)}` : undefined}>
      <div className="min-w-0 flex-1">
        {/* Título e selos */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h4 className="min-w-0 truncate text-sm font-medium text-slate-900 sm:text-[15px]">{entry.description}</h4>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusStyle(entry.status)}`}>{entry.status}</span>
          {entry.category && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{entry.category}</span>
          )}
          {entry.is_installment && entry.installments_count > 1 && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{entry.installments_count}x</span>
          )}
          {isByEvent && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">
              Por evento{entryCountsInOperationalFinance(entry) ? '' : ' · fora do fluxo'}
            </span>
          )}
          {entry.is_negotiating && (
            <span className="rounded-full bg-orange-50 px-2 py-0.5 text-[11px] text-orange-700">Em negociação</span>
          )}
        </div>

        {/* Detalhes */}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
          {dateInfo && <span className={dateInfo.className}>{dateInfo.text}</span>}
          {entry.clients?.name && (
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" />
              <span className="font-medium text-slate-700">{entry.clients.name}</span>
            </span>
          )}
          {processTitle && (
            <button
              type="button"
              onClick={() => onOpenProcess(entry)}
              className="inline-flex min-w-0 max-w-full items-center gap-1 text-left text-[#7a6440] hover:text-[#5c4a2e] hover:underline"
              title={processTitle}
            >
              <FileText className="h-3 w-3 shrink-0" />
              <span className="truncate">{processTitle}</span>
            </button>
          )}
        </div>
        {entry.payment_notes && (
          <p className="mt-0.5 truncate text-[11px] text-slate-400" title={entry.payment_notes}>
            {entry.payment_notes}
          </p>
        )}
      </div>

      {/* Valor e ações */}
      <div className="flex shrink-0 items-center gap-2">
        <span className={`whitespace-nowrap text-base font-semibold tabular-nums ${isRevenue ? 'text-emerald-600' : 'text-red-600'}`}>
          {isRevenue ? '+' : '−'} {brl(entry.amount)}
        </span>
        <div className="flex items-center">
          {expandable && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-[#c4ab83]/10 hover:text-[#7a6440]"
              title={expanded ? 'Ocultar parcelas' : 'Ver parcelas'}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          )}
          <button
            type="button"
            onClick={() => onEdit(entry)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            title="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(entry.id)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
            title="Excluir"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
